import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Copyright 1999-2020. Plesk International GmbH. All rights reserved.
import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import Measure from 'react-measure';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { CLS_PREFIX } from '../../constants';
var propTypes = {
  /**
   * Type of the `Layout`
   * @since 1.4.0
   */
  type: PropTypes.oneOf(['fluid', 'simplified']),

  /**
   * Header content.
   * @since 1.4.0
   */
  header: PropTypes.node,

  /**
   * Define header position - always on top (fixed) or static.
   * @since 1.4.0
   */
  headerFixed: PropTypes.bool,

  /**
   * Additional content at the top of the header.
   * @since 3.2.0
   */
  headerAddon: PropTypes.node,

  /**
   * Sidebar content.
   * @since 1.4.0
   */
  sidebar: PropTypes.node,

  /**
   * Sidebar type.
   * @since 3.2.0
   */
  sidebarType: PropTypes.oneOf(['collapsed', 'folded']),

  /**
   * Define if sidebar can collapse.
   * @since 1.4.0
   */
  sidebarCollapsible: PropTypes.bool,

  /**
   * Sidebar collapsed state.
   * @since 1.4.0
   */
  sidebarCollapsed: PropTypes.bool,

  /**
   * Additional content between header and main content.
   * @since 1.4.0
   */
  contentAddon: PropTypes.node,

  /**
   * Content header.
   * @since 3.0.0
   */
  contentHeader: PropTypes.node,

  /**
   * Footer content.
   * @since 1.4.0
   */
  footer: PropTypes.node,

  /**
   * Main content of the `Layout`.
   * @since 1.4.0
   */
  children: PropTypes.node,

  /**
   * Width of the content
   * @since 3.0.0
   */
  width: PropTypes.oneOf(['sm', 'md', 'lg']),

  /**
   * Sidebar onToggle handler.
   * @since 1.4.0
   */
  onSidebarToggle: PropTypes.func,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string
};
var defaultProps = {
  type: 'fluid',
  header: undefined,
  headerFixed: true,
  headerAddon: undefined,
  sidebar: undefined,
  sidebarType: 'collapsed',
  sidebarCollapsible: true,
  sidebarCollapsed: false,
  contentAddon: undefined,
  contentHeader: undefined,
  footer: undefined,
  width: undefined,
  onSidebarToggle: undefined,
  children: undefined,
  className: undefined,
  baseClassName: "".concat(CLS_PREFIX, "layout")
};

var subscribeMatchMedia = function subscribeMatchMedia(query, handler) {
  var mq = window.matchMedia(query);
  handler(mq);
  mq.addListener(handler);
  return function () {
    mq.removeListener(handler);
  };
};

var subscribeClickOutside = function subscribeClickOutside(refs, handler) {
  var listener = function listener(event) {
    if (refs.some(function (_ref) {
      var current = _ref.current;
      return !current || current.contains(event.target);
    })) {
      return;
    }

    handler(event);
  };

  document.addEventListener('mousedown', listener);
  document.addEventListener('touchstart', listener);
  return function () {
    document.removeEventListener('mousedown', listener);
    document.removeEventListener('touchstart', listener);
  };
};
/**
 * @since 1.4.0
 */


var Layout =
/*#__PURE__*/
function (_Component) {
  _inherits(Layout, _Component);

  function Layout(props) {
    var _this;

    _classCallCheck(this, Layout);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Layout).call(this, props));

    _defineProperty(_assertThisInitialized(_this), "state", {
      isSidebarClosedResponsive: true,
      headerHeight: null,
      isResponsive: false
    });

    _defineProperty(_assertThisInitialized(_this), "handleMatchMedia", function (_ref2) {
      var matches = _ref2.matches;

      _this.setState({
        isResponsive: matches
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handleClickOutside", function () {
      var isResponsive = _this.state.isResponsive;

      if (isResponsive) {
        _this.setState({
          isSidebarClosedResponsive: true
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleToggle", function () {
      _this.toggleSidebarClosed();
    });

    _defineProperty(_assertThisInitialized(_this), "handleResize", function (_ref3) {
      var bounds = _ref3.bounds;

      _this.setState({
        headerHeight: bounds.height
      });
    });

    _defineProperty(_assertThisInitialized(_this), "getSidebarClosed", function () {
      var _this$state = _this.state,
          isResponsive = _this$state.isResponsive,
          isSidebarClosedResponsive = _this$state.isSidebarClosedResponsive,
          isSidebarClosedDesktop = _this$state.isSidebarClosedDesktop;
      return isResponsive ? isSidebarClosedResponsive : isSidebarClosedDesktop;
    });

    _defineProperty(_assertThisInitialized(_this), "toggleSidebarClosed", function () {
      _this.setState(function (prevState) {
        var key = prevState.isResponsive ? 'isSidebarClosedResponsive' : 'isSidebarClosedDesktop';
        return _defineProperty({}, key, !prevState[key]);
      }, function () {
        if (_this.props.onSidebarToggle) {
          _this.props.onSidebarToggle(_this.getSidebarClosed());
        }
      });
    });

    _this.headerRef = createRef();
    _this.sidebarRef = createRef();
    _this.responsiveSidebarTogglerRef = createRef();
    return _this;
  }

  _createClass(Layout, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.unsubscribeMatchMedia = subscribeMatchMedia('(max-width: 1022px)', this.handleMatchMedia);
      this.unsubscribeClickOutside = subscribeClickOutside([this.sidebarRef, this.responsiveSidebarTogglerRef], this.handleClickOutside);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.unsubscribeMatchMedia();
      this.unsubscribeClickOutside();
    }
  }, {
    key: "renderHeader",
    value: function renderHeader(_ref5) {
      var _this2 = this;

      var header = _ref5.header;
      var _this$props = this.props,
          baseClassName = _this$props.baseClassName,
          headerAddon = _this$props.headerAddon,
          sidebar = _this$props.sidebar;
      var headerHeight = this.state.headerHeight;

      if (!header) {
        return null;
      }

      var isSidebarClosed = this.getSidebarClosed();
      return React.createElement(CSSTransition, {
        in: !isSidebarClosed,
        timeout: 300,
        classNames: {
          enter: "".concat(baseClassName, "__header--enter"),
          enterActive: "".concat(baseClassName, "__header--active-enter"),
          enterDone: "".concat(baseClassName, "__header--on"),
          exit: "".concat(baseClassName, "__header--exit"),
          exitActive: "".concat(baseClassName, "__header--active-exit"),
          exitDone: "".concat(baseClassName, "__header--off")
        },
        nodeRef: this.headerRef
      }, React.createElement("header", {
        className: "".concat(baseClassName, "__header"),
        ref: this.headerRef,
        style: {
          height: "".concat(headerHeight, "px")
        }
      }, React.createElement(Measure, {
        onResize: this.handleResize,
        bounds: true
      }, function (_ref6) {
        var measureRef = _ref6.measureRef;
        return React.createElement("div", {
          ref: measureRef,
          className: "".concat(baseClassName, "__header-inner")
        }, headerAddon ? React.createElement("div", {
          className: "".concat(baseClassName, "__header-addon")
        }, headerAddon) : null, React.createElement("div", {
          className: "".concat(baseClassName, "__header-content")
        }, sidebar && React.createElement("button", {
          className: classNames("".concat(baseClassName, "__responsive-sidebar-toggler"), _defineProperty({}, "".concat(baseClassName, "__responsive-sidebar-toggler--open"), !isSidebarClosed)),
          type: "button",
          onClick: _this2.handleToggle,
          ref: _this2.responsiveSidebarTogglerRef
        }, React.createElement("span", {
          className: "".concat(baseClassName, "__responsive-sidebar-toggler-inner")
        }, React.createElement("span", {
          className: "".concat(baseClassName, "__responsive-sidebar-toggler-icon")
        }))), React.createElement("div", {
          className: "".concat(baseClassName, "__header-content-inner")
        }, header)));
      })));
    }
  }, {
    key: "renderContentAddon",
    value: function renderContentAddon(_ref7) {
      var contentAddon = _ref7.contentAddon;
      var baseClassName = this.props.baseClassName;

      if (!contentAddon) {
        return null;
      }

      return React.createElement("div", {
        className: "".concat(baseClassName, "__content-addon")
      }, React.createElement("div", {
        className: "".concat(baseClassName, "__content-addon-inner")
      }, contentAddon));
    }
  }, {
    key: "renderContentHeader",
    value: function renderContentHeader(_ref8) {
      var contentHeader = _ref8.contentHeader;
      var baseClassName = this.props.baseClassName;

      if (!contentHeader) {
        return null;
      }

      return React.createElement("div", {
        className: "".concat(baseClassName, "__content-header")
      }, React.createElement("div", {
        className: "".concat(baseClassName, "__content-header-inner")
      }, contentHeader));
    }
  }, {
    key: "renderSidebar",
    value: function renderSidebar(_ref9) {
      var _classNames2;

      var sidebar = _ref9.sidebar;
      var _this$props2 = this.props,
          baseClassName = _this$props2.baseClassName,
          sidebarCollapsible = _this$props2.sidebarCollapsible,
          type = _this$props2.type;

      if (!sidebar || type === 'simplified') {
        return null;
      }

      var isSidebarClosed = this.getSidebarClosed();
      return React.createElement(CSSTransition, {
        in: !isSidebarClosed,
        timeout: 300,
        classNames: {
          enter: "".concat(baseClassName, "__sidebar--enter"),
          enterActive: "".concat(baseClassName, "__sidebar--active-enter"),
          enterDone: "".concat(baseClassName, "__sidebar--on"),
          exit: "".concat(baseClassName, "__sidebar--exit"),
          exitActive: "".concat(baseClassName, "__sidebar--active-exit"),
          exitDone: "".concat(baseClassName, "__sidebar--off")
        }
      }, React.createElement("aside", {
        className: classNames("".concat(baseClassName, "__sidebar"), (_classNames2 = {}, _defineProperty(_classNames2, "".concat(baseClassName, "__sidebar--on"), !isSidebarClosed), _defineProperty(_classNames2, "".concat(baseClassName, "__sidebar--off"), isSidebarClosed), _classNames2)),
        ref: this.sidebarRef
      }, React.createElement("div", {
        className: "".concat(baseClassName, "__sidebar-inner")
      }, sidebarCollapsible && React.createElement("div", {
        className: "".concat(baseClassName, "__sidebar-toggler"),
        onClick: this.handleToggle
      }, React.createElement("div", {
        className: "".concat(baseClassName, "__sidebar-toggler-inner")
      })), React.createElement("div", {
        className: "".concat(baseClassName, "__sidebar-content")
      }, React.createElement("div", {
        className: "".concat(baseClassName, "__sidebar-content-inner")
      }, sidebar)))));
    }
  }, {
    key: "renderMainContent",
    value: function renderMainContent(_ref10) {
      var children = _ref10.children;
      var baseClassName = this.props.baseClassName;
      return React.createElement("main", {
        className: "".concat(baseClassName, "__main")
      }, React.createElement("div", {
        className: "".concat(baseClassName, "__main-inner")
      }, children));
    }
  }, {
    key: "renderFooter",
    value: function renderFooter(_ref11) {
      var footer = _ref11.footer;
      var baseClassName = this.props.baseClassName;

      if (!footer) {
        return null;
      }

      return React.createElement("footer", {
        className: "".concat(baseClassName, "__footer")
      }, React.createElement("div", {
        className: "".concat(baseClassName, "__footer-inner")
      }, footer));
    }
  }, {
    key: "render",
    value: function render() {
      var _classNames3;

      var _this$props3 = this.props,
          baseClassName = _this$props3.baseClassName,
          className = _this$props3.className,
          children = _this$props3.children,
          type = _this$props3.type,
          header = _this$props3.header,
          headerFixed = _this$props3.headerFixed,
          headerAddon = _this$props3.headerAddon,
          sidebar = _this$props3.sidebar,
          sidebarType = _this$props3.sidebarType,
          sidebarCollapsible = _this$props3.sidebarCollapsible,
          sidebarCollapsed = _this$props3.sidebarCollapsed,
          onSidebarToggle = _this$props3.onSidebarToggle,
          contentAddon = _this$props3.contentAddon,
          contentHeader = _this$props3.contentHeader,
          footer = _this$props3.footer,
          widthProp = _this$props3.width,
          props = _objectWithoutProperties(_this$props3, ["baseClassName", "className", "children", "type", "header", "headerFixed", "headerAddon", "sidebar", "sidebarType", "sidebarCollapsible", "sidebarCollapsed", "onSidebarToggle", "contentAddon", "contentHeader", "footer", "width"]);

      var width = widthProp;
      var headerPositionFixed = headerFixed;

      if (type === 'simplified') {
        headerPositionFixed = false;

        if (!width) {
          width = 'sm';
        }
      }

      var isSidebarClosed = this.getSidebarClosed();
      return React.createElement("div", _extends({
        className: classNames(baseClassName, (_classNames3 = {}, _defineProperty(_classNames3, "".concat(baseClassName, "--").concat(type), type), _defineProperty(_classNames3, "".concat(baseClassName, "--").concat(width), width), _defineProperty(_classNames3, "".concat(baseClassName, "--touch"), 'ontouchstart' in document.documentElement), _defineProperty(_classNames3, "".concat(baseClassName, "--header"), header), _defineProperty(_classNames3, "".concat(baseClassName, "--header-fixed"), header && headerPositionFixed), _defineProperty(_classNames3, "".concat(baseClassName, "--sidebar"), sidebar), _defineProperty(_classNames3, "".concat(baseClassName, "--sidebar-type-").concat(sidebarType), sidebarType && sidebar), _defineProperty(_classNames3, "".concat(baseClassName, "--sidebar-collapsed"), sidebarType === 'collapsed' && isSidebarClosed), _defineProperty(_classNames3, "".concat(baseClassName, "--sidebar-folded"), sidebarType === 'folded' && isSidebarClosed), _classNames3), className)
      }, props), React.createElement("div", {
        className: "".concat(baseClassName, "__inner")
      }, this.renderHeader({
        header: header
      }), React.createElement("div", {
        className: "".concat(baseClassName, "__container")
      }, this.renderSidebar({
        sidebar: sidebar,
        headerFixed: headerPositionFixed
      }), React.createElement("div", {
        className: "".concat(baseClassName, "__content")
      }, this.renderContentAddon({
        contentAddon: contentAddon
      }), this.renderContentHeader({
        contentHeader: contentHeader
      }), this.renderMainContent({
        children: children
      }), this.renderFooter({
        footer: footer
      })))));
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(props, state) {
      var sidebarCollapsed = props.sidebarCollapsed;
      var prevSidebarCollapsed = state.prevSidebarCollapsed;
      var nextState = {};

      if (sidebarCollapsed !== prevSidebarCollapsed) {
        nextState.isSidebarClosedDesktop = sidebarCollapsed;
      }

      nextState.prevSidebarCollapsed = sidebarCollapsed;
      return nextState;
    }
  }]);

  return Layout;
}(Component);

export { Layout as default };
Layout.propTypes = propTypes;
Layout.defaultProps = defaultProps;